import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  TextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'

import { isEmpty, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'
import { useFocusVisible } from 'use-focus-visible'

import {
  clearSearchButtonLabel,
  removeSelectionButtonLabel,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'

import { TypeAheadCustomOptionsConfig } from './TypeAheadCustomOptionsConfig'
import { AccessibilityRolesNative, IS_WEB } from '../../../constants'
import { getFocusBoxShadow, getInputFieldStyles } from '../../../styles/commonStyles'
import { getFontStyles } from '../../../styles/typeStyles'
import { getAuroraInputProps } from '../../../utils/themes/auroraInput'
import { ThemeType } from '../../../utils/themes/ThemeProvider'
import { tID } from '../../../utils/utils'
import { BodyText, Size } from '../../bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../icons/ChevronIcon'
import { CloseCircleIcon } from '../../icons/CloseCircleIcon'
import { CloseIcon } from '../../icons/CloseIcon'
import { LoadingSpinner } from '../../icons/LoadingSpinner'
import { SearchIcon } from '../../icons/SearchIcon'

const InputFieldContainer = styled.Pressable<{ active?: boolean; readOnly: boolean }>(({ active, readOnly }) => ({
  ...(IS_WEB && { cursor: active || readOnly ? 'default' : 'pointer' }),
  ...(IS_WEB && { outlineWidth: '0', outlineStyle: 'none' }),
  flexDirection: 'row',
  alignItems: 'center',
  height: 'auto',
  minHeight: 'auto',
  maxHeight: 'auto',
}))

const Input = styled(TextInput)<{ hidden: boolean; padTop: boolean }>(({ hidden, padTop }) => ({
  marginRight: '8px',
  marginTop: padTop ? '8px' : '0px',
  flexGrow: 1,
  ...(IS_WEB && { outlineWidth: '0' }),
  ...(IS_WEB && { outlineStyle: 'none' }),
  opacity: hidden ? 0 : 100,
  ...(hidden && {
    width: 0,
  }),
  minHeight: '24px',
}))

const RightIconContainer = styled.View({
  marginLeft: 'auto',
  flexDirection: 'row',
  paddingLeft: '8px',
})

const SelectedAndInputContainer = styled.View<{ multiSelect: boolean }>(({ multiSelect }) => ({
  flexDirection: 'row',
  flex: 1,
  ...(multiSelect && { flexWrap: 'wrap' }),
}))

const SelectedOptionTextContainer = styled.View({
  flexWrap: 'wrap',
  flexShrink: 1,
  flexGrow: 1,
})

const ClearButton = styled(Pressable)({
  ...(IS_WEB && { outlineWidth: 0 }),
  borderRadius: '3px',
})

const SelectedContainer = styled.View({
  margin: '-2px',
  flexDirection: 'row',
  flexShrink: 1,
  flexWrap: 'wrap',
  display: 'flex',
})

const SelectedAlwaysOpenContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
  flex: 1,
}))

const SelectedDescriptionContainer = styled.View({
  flex: 1,
  flexShrink: 1,
})

const SelectedOption = styled(Pressable)<{ theme: ThemeType }>(({ theme }) => ({
  borderRadius: '4px',
  backgroundColor: theme.colors.secondaryTertiaryButtonBackgroundDefault,
  padding: '3px 7px 3px 11px',
  flexDirection: 'row',
  alignItems: 'center',
  margin: '2px',
  marginRight: '8px',
  display: 'flex',
  flexBasis: Platform.OS === 'web' ? 'fit-content' : '',
}))

const SelectedOptionFixedWidth = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  borderRadius: '4px',
  backgroundColor: theme.colors.secondaryTertiaryButtonBackgroundDefault,
  padding: '5px 12px',
  alignItems: 'center',
  justifyContent: 'center',
  width: 75,
}))

const CloseIconContainer = styled(Pressable)({
  marginLeft: '4px',
  height: '16px',
  width: '16px',
  alignItems: 'center',
  justifyContent: 'center',
})

const SearchIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const TypeAheadInputWithGeneric = <T,>(
  {
    placeholder,
    value,
    inputValue,
    onChange,
    onBlur = noop,
    onFocus = noop,
    onPress,
    isFocused,
    isLoading,
    editable = true,
    onKeyPress,
    name,
    onDeleteItem,
    style,
    multiSelect = true,
    singleSelectModeSelectedItemLabel,
    accessibilityLabel,
    id,
    showSearchIcon = true,
    customOptionsConfig,
    isDropdownOpen,
    listboxId,
    activeDescendantId,
    showLoadingIndicator,
    autoComplete,
    alwaysOpen = false,
    readOnly = false,
    error,
  }: TypeAheadInputProps<T>,
  ref: ForwardedRef<TypeAheadInputRef>,
) => {
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef?.current?.focus()
    },
    focusTrigger: () => {
      triggerRef.current?.focus()
    },
    isFocused: () => inputRef.current?.isFocused() ?? false,
  }))
  const { colors, fontFamilyMetadata, componentStyles, isAuroraTheme } = useTheme()
  const { formatMessage } = useIntl()
  const { focusVisible: clearFocusVisible, onBlur: onClearBlur, onFocus: onClearFocus } = useFocusVisible()
  const { focusVisible: triggerFocusVisible, onBlur: onTriggerBlur, onFocus: onTriggerFocus } = useFocusVisible()
  const clearRef = useRef<any>(null)
  const inputRef = useRef<TextInput>(null)
  const triggerRef = useRef<View>(null)
  const [searchValue, setSearchValue] = useState<string>(inputValue)
  /** Pad input if it wraps around due to multiple selected options  */
  const [padInputTop, setPadInputTop] = useState(false)
  const showClearButton = !isEmpty(searchValue) && !isLoading && isFocused
  const isDisplayingSingleSelectedOption = !multiSelect && !isEmpty(value) && !isFocused
  const showChevron = isDisplayingSingleSelectedOption

  const textInputStyles = getInputFieldStyles({
    colors,
    fontFamilyMetadata,
    componentStyles,
    active: isFocused,
    readOnly,
    error: !!error,
  }) as ViewStyle

  useEffect(() => {
    setSearchValue(inputValue)
  }, [inputValue])

  const handleOnLayoutInput = (event: LayoutChangeEvent) => {
    setPadInputTop(event.nativeEvent.layout.y > 8)
  }

  const handleMouseDown = (event: any) => {
    if (
      event.button === 0 &&
      inputRef.current?.isFocused() &&
      !event.target.closest('input, button, [role="button"]')
    ) {
      event.preventDefault()
    }
  }

  const getSelectedOptionsDisplay = () => {
    if (!isEmpty(value)) {
      // For alwaysOpen mode with single-select, render code in chip + description and X outside
      if (alwaysOpen && !multiSelect) {
        return (
          <SelectedContainer testID={tID('typeAheadInput-selectedContainer')}>
            {value.map((item: string) => {
              // Extract the code portion (first word) and description (rest)
              const spaceIndex = item.indexOf(' ')
              const codeOnly = spaceIndex > -1 ? item.substring(0, spaceIndex) : item
              const description = spaceIndex > -1 ? item.substring(spaceIndex + 1) : ''
              return (
                <SelectedAlwaysOpenContainer key={item} testID={tID('typeAheadInput-selectedOption')}>
                  <SelectedOptionFixedWidth>
                    <BodyText text={codeOnly} size={Size.BADGE} testID={tID('typeAheadInput-selection')} />
                  </SelectedOptionFixedWidth>
                  {description && (
                    <SelectedDescriptionContainer>
                      <BodyText text={description} size={Size.BADGE} testID={tID('typeAheadInput-selection-desc')} />
                    </SelectedDescriptionContainer>
                  )}
                  <CloseIconContainer
                    onPress={() => {
                      onDeleteItem(item)
                    }}
                    testID={tID('typeAheadInput-selection-delete')}
                    accessibilityRole='button'
                    accessibilityLabel={formatMessage(removeSelectionButtonLabel)}
                  >
                    <CloseIcon size={7.5} fillColor={colors.iconDefault} />
                  </CloseIconContainer>
                </SelectedAlwaysOpenContainer>
              )
            })}
          </SelectedContainer>
        )
      } else if (multiSelect) {
        // Multi-select: render as chips with full labels
        return (
          <SelectedContainer testID={tID('typeAheadInput-selectedContainer')}>
            {value.map((item: string) => {
              return (
                <SelectedOption key={item} testID={tID('typeAheadInput-selectedOption')}>
                  <BodyText
                    text={customOptionsConfig?.getLabelForValue(item) || item}
                    size={Size.BADGE}
                    testID={tID('typeAheadInput-selection')}
                  />
                  {!readOnly && (
                    <CloseIconContainer
                      onPress={() => {
                        onDeleteItem(item)
                      }}
                      testID={tID('typeAheadInput-selection-delete')}
                      accessibilityRole='button'
                      accessibilityLabel={formatMessage(removeSelectionButtonLabel)}
                    >
                      <CloseIcon size={7.5} fillColor={colors.iconDefault} />
                    </CloseIconContainer>
                  )}
                </SelectedOption>
              )
            })}
          </SelectedContainer>
        )
      } else if (!isFocused) {
        // Standard single-select: show value when not focused
        return (
          (singleSelectModeSelectedItemLabel && (
            <SelectedOptionTextContainer testID={tID('typeAheadInput-singleSelection')}>
              {singleSelectModeSelectedItemLabel}
            </SelectedOptionTextContainer>
          )) || (
            <BodyText
              testID={tID('typeAheadInput-singleSelection')}
              text={customOptionsConfig?.getLabelForValue(value[0]) || value[0]}
            />
          )
        )
      }
    }
    return <></>
  }

  return (
    <InputFieldContainer
      ref={triggerRef}
      {...(IS_WEB ? { onMouseDown: handleMouseDown } : {})}
      onPress={() => {
        inputRef?.current?.focus()
        onPress()
      }}
      testID={tID(`${name}-trigger`)}
      nativeID={id ? `${id}-trigger` : undefined}
      active={isFocused}
      onFocus={onTriggerFocus}
      onBlur={onTriggerBlur}
      style={[textInputStyles, style, triggerFocusVisible && getFocusBoxShadow({ colors })]}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={AccessibilityRolesNative.SEARCH}
      readOnly={readOnly}
    >
      <>
        {showSearchIcon && (isEmpty(value) || (!multiSelect && isFocused)) && !readOnly && (
          <SearchIconContainer>
            <SearchIcon fillColor={colors.iconDefault} />
          </SearchIconContainer>
        )}
        <SelectedAndInputContainer multiSelect={multiSelect}>
          {getSelectedOptionsDisplay()}
          <Input
            {...getAuroraInputProps(isAuroraTheme)}
            onPressOut={onPress}
            placeholder={isEmpty(value) && !readOnly ? placeholder : undefined}
            placeholderTextColor={colors.inputTextPlaceholder}
            value={searchValue}
            onChangeText={(text: string) => {
              setSearchValue(text)
              onChange(text)
            }}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={inputRef}
            editable={editable && !readOnly}
            onKeyPress={onKeyPress}
            onSubmitEditing={() => {
              onKeyPress(undefined, true)
            }}
            testID={tID(name)}
            style={{ ...getFontStyles(colors, fontFamilyMetadata).body.default, lineHeight: 20 } as TextStyle}
            hidden={isDisplayingSingleSelectedOption}
            accessibilityLabel={accessibilityLabel}
            onLayout={handleOnLayoutInput}
            padTop={padInputTop}
            nativeID={id}
            {...(IS_WEB
              ? {
                  id,
                  name,
                  role: 'combobox',
                  tabIndex: isDisplayingSingleSelectedOption ? -1 : 0,
                  'aria-controls': listboxId,
                  'aria-expanded': isFocused ? !!isDropdownOpen : false,
                  'aria-activedescendant': activeDescendantId,
                  'aria-autocomplete': 'list',
                  ...(autoComplete && { autoComplete: autoComplete as TextInputProps['autoComplete'] }),
                }
              : {})}
          />
        </SelectedAndInputContainer>
        <RightIconContainer>
          {((isLoading && isFocused) || showLoadingIndicator) && <LoadingSpinner />}
          {showClearButton && (
            <ClearButton
              ref={clearRef}
              onPress={() => {
                onChange('')
                setSearchValue('')
                inputRef?.current?.focus()
              }}
              testID={tID('typeAheadInput-clear-button')}
              onFocus={onClearFocus}
              onBlur={onClearBlur}
              style={clearFocusVisible && getFocusBoxShadow({ colors })}
              accessibilityRole='button'
              accessibilityLabel={formatMessage(clearSearchButtonLabel)}
            >
              <CloseCircleIcon />
            </ClearButton>
          )}
          {showChevron && !readOnly && (
            <ChevronIcon size={18} fillColor={colors.iconDefault} direction={ChevronIconDirection.DOWN} />
          )}
        </RightIconContainer>
      </>
    </InputFieldContainer>
  )
}

export interface TypeAheadInputProps<T> {
  placeholder?: string
  /** Selected items */
  value: string[]
  inputValue: string
  onChange: (text: string) => void
  onFocus?: () => void
  onBlur?: (e?: any) => void
  onPress: () => void
  isFocused?: boolean
  isLoading?: boolean
  editable?: boolean
  onKeyPress: (e?: NativeSyntheticEvent<TextInputKeyPressEventData>, isSubmit?: boolean) => void
  name?: string
  onDeleteItem: (item: string) => void
  style?: ViewStyle
  multiSelect?: boolean
  singleSelectModeSelectedItemLabel?: JSX.Element
  accessibilityLabel?: string
  id?: string
  showSearchIcon?: boolean
  customOptionsConfig?: TypeAheadCustomOptionsConfig<T>
  isDropdownOpen?: boolean
  listboxId?: string
  activeDescendantId?: string
  showLoadingIndicator?: boolean
  autoComplete?: TextInputProps['autoComplete'] | 'country-name'
  /** Keep dropdown always open - affects how single-select displays selected values */
  alwaysOpen?: boolean
  readOnly?: boolean
  error?: string
}

export type TypeAheadInputRef = {
  focus: () => void
  focusTrigger: () => void
  isFocused: () => boolean
}

export const TypeAheadInput = forwardRef(TypeAheadInputWithGeneric) as <T>(
  props: TypeAheadInputProps<T> & { ref?: React.Ref<TypeAheadInputRef> },
) => JSX.Element
